import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Questionnaire() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    writing_style: 'casual',
    tone: 'warm',
    favorite_topics: [],
    relationship: '',
    recipient_name: '',
    recipient_address: {
      name: '',
      address_line1: '',
      address_city: '',
      address_state: '',
      address_zip: ''
    }
  });
  const [topicInput, setTopicInput] = useState('');

  const addTopic = () => {
    if (topicInput.trim()) {
      setFormData({ ...formData, favorite_topics: [...formData.favorite_topics, topicInput.trim()] });
      setTopicInput('');
    }
  };

  const removeTopic = (index) => {
    setFormData({ ...formData, favorite_topics: formData.favorite_topics.filter((_, i) => i !== index) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/profile/questionnaire`, formData, { withCredentials: true });
      toast.success('Profile saved! Redirecting to dashboard...');
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (error) {
      toast.error('Failed to save questionnaire');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#F9F7F3] py-12 px-6">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-light tracking-tight mb-2" style={{fontFamily: 'Spectral, serif'}}>Tell Us About You</h1>
          <p className="text-base text-[#737373]">Help us write letters that sound authentically like you.</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white border border-[#E5E0D8] rounded-md p-8 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]" data-testid="questionnaire-form">
          <div className="space-y-6">
            <div>
              <Label htmlFor="writing_style" className="label-text">Writing Style</Label>
              <Select value={formData.writing_style} onValueChange={(val) => setFormData({...formData, writing_style: val})}>
                <SelectTrigger className="mt-2 border-[#E5E0D8]" data-testid="writing-style-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="casual">Casual & Friendly</SelectItem>
                  <SelectItem value="formal">Formal & Respectful</SelectItem>
                  <SelectItem value="heartfelt">Heartfelt & Emotional</SelectItem>
                  <SelectItem value="humorous">Light & Humorous</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="tone" className="label-text">Tone</Label>
              <Select value={formData.tone} onValueChange={(val) => setFormData({...formData, tone: val})}>
                <SelectTrigger className="mt-2 border-[#E5E0D8]" data-testid="tone-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="warm">Warm & Supportive</SelectItem>
                  <SelectItem value="upbeat">Upbeat & Positive</SelectItem>
                  <SelectItem value="encouraging">Encouraging</SelectItem>
                  <SelectItem value="nostalgic">Nostalgic</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="label-text">Favorite Topics to Discuss</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  value={topicInput}
                  onChange={(e) => setTopicInput(e.target.value)}
                  placeholder="e.g., family, sports, movies"
                  className="border-[#E5E0D8]"
                  data-testid="topic-input"
                />
                <Button type="button" onClick={addTopic} variant="outline" className="border-[#E5E0D8]" data-testid="add-topic-btn">Add</Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {formData.favorite_topics.map((topic, idx) => (
                  <span key={idx} className="bg-[#E5E0D8] px-3 py-1 rounded-full text-sm flex items-center gap-2" data-testid={`topic-${idx}`}>
                    {topic}
                    <button type="button" onClick={() => removeTopic(idx)} className="text-[#B94A3E]">×</button>
                  </span>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="relationship" className="label-text">Your Relationship</Label>
              <Input
                id="relationship"
                value={formData.relationship}
                onChange={(e) => setFormData({...formData, relationship: e.target.value})}
                placeholder="e.g., spouse, parent, friend"
                className="mt-2 border-[#E5E0D8]"
                required
                data-testid="relationship-input"
              />
            </div>

            <div>
              <Label htmlFor="recipient_name" className="label-text">Recipient's Name</Label>
              <Input
                id="recipient_name"
                value={formData.recipient_name}
                onChange={(e) => setFormData({...formData, recipient_name: e.target.value})}
                className="mt-2 border-[#E5E0D8]"
                required
                data-testid="recipient-name-input"
              />
            </div>

            <div className="border-t border-[#E5E0D8] pt-6 mt-6">
              <h3 className="text-xl mb-4" style={{fontFamily: 'Spectral, serif'}}>Recipient's Mailing Address</h3>
              <div className="space-y-4">
                <div>
                  <Label className="label-text">Full Name</Label>
                  <Input
                    value={formData.recipient_address.name}
                    onChange={(e) => setFormData({...formData, recipient_address: {...formData.recipient_address, name: e.target.value}})}
                    className="mt-2 border-[#E5E0D8]"
                    required
                    data-testid="address-name-input"
                  />
                </div>
                <div>
                  <Label className="label-text">Address Line</Label>
                  <Input
                    value={formData.recipient_address.address_line1}
                    onChange={(e) => setFormData({...formData, recipient_address: {...formData.recipient_address, address_line1: e.target.value}})}
                    className="mt-2 border-[#E5E0D8]"
                    required
                    data-testid="address-line-input"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="label-text">City</Label>
                    <Input
                      value={formData.recipient_address.address_city}
                      onChange={(e) => setFormData({...formData, recipient_address: {...formData.recipient_address, address_city: e.target.value}})}
                      className="mt-2 border-[#E5E0D8]"
                      required
                      data-testid="address-city-input"
                    />
                  </div>
                  <div>
                    <Label className="label-text">State</Label>
                    <Input
                      value={formData.recipient_address.address_state}
                      onChange={(e) => setFormData({...formData, recipient_address: {...formData.recipient_address, address_state: e.target.value}})}
                      className="mt-2 border-[#E5E0D8]"
                      required
                      data-testid="address-state-input"
                    />
                  </div>
                  <div>
                    <Label className="label-text">Zip Code</Label>
                    <Input
                      value={formData.recipient_address.address_zip}
                      onChange={(e) => setFormData({...formData, recipient_address: {...formData.recipient_address, address_zip: e.target.value}})}
                      className="mt-2 border-[#E5E0D8]"
                      required
                      data-testid="address-zip-input"
                    />
                  </div>
                </div>
              </div>
            </div>

            <Button type="submit" className="w-full bg-[#B94A3E] hover:bg-[#9d3e33] text-white" disabled={loading} data-testid="questionnaire-submit-btn">
              {loading ? 'Saving...' : 'Save & Continue'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
